import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Boxes, SlidersHorizontal, Plug } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { Button } from '@retink/web-ui'
import { Badge, type BadgeProps } from '@retink/web-ui'
import { Card, CardHeader, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@retink/web-ui'
import { PageHeader } from '@retink/web-ui'
import { EmptyState, TableEmpty } from '@retink/web-ui'
import { LoadingState, ErrorState } from '@retink/web-ui'
import { Switch } from '@retink/web-ui'
import { ConfirmDestructive } from '@retink/web-ui'
import { toast } from '@retink/web-ui'
import { useResource, useMutation } from '@retink/web-ui'
import { FormError } from '@retink/web-ui'
import { Input, Field } from '@retink/web-ui'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@retink/web-ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@retink/web-ui'
import { RowActions, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@retink/web-ui'

// Platform-staff entitlement + sign-in config for a single tenant, reached as
// sections of the Tenant Workspace (/t/:tenantId/*). All read path-scoped
// /api/admin/*tenant/:tenantId, so they work for staff regardless of the cookie.

const statusTone = (s: string): BadgeProps['tone'] =>
  s === 'active' ? 'success' : s === 'trial' ? 'primary' : s === 'invited' ? 'warning' : s === 'suspended' ? 'danger' : 'neutral'

interface Tenant { id: string; name: string; status: string; org_id: string; org_name: string; allow_local_identities: boolean }
interface Product { id: string; display_name: string; status: string }
interface Feature { feature_id: string; feature_key: string; name: string; category: string; enabled: boolean; override_enabled: boolean | null }
interface Adapter {
  id: string; adapter_type_id: string; instance_name: string; type_name: string
  category: string; status: string; config: Record<string, unknown>; secret_ref: string | null
}
interface AdapterType { id: string; key: string; name: string; category: string; secret_keys: string[] }
interface OrgIdp { id: string; kind: string; name: string; domains: string[]; status: string }

// ── Products ────────────────────────────────────────────────────────────────

// Read-only catalog. Product-to-tenant entitlements are DELETED (the locked
// 3-role model): every tenant is granted every product, so there is nothing to
// provision per tenant. This just shows the platform catalog for context.
export function TenantProducts() {
  const [all, setAll] = useState<Product[]>([])
  useEffect(() => {
    api.get<Product[]>('/api/admin/products').then(setAll).catch(() => {})
  }, [])

  return (
    <div className="p-8">
      <PageHeader title="Products" description="Every tenant is granted every product — this is the platform catalog, not a per-tenant assignment." />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {['Product', 'Status'].map((h, i) => <TableHead key={h || i}>{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {all.length === 0 && (
              <TableEmpty colSpan={2}>
                <EmptyState icon={Boxes} title="No products in the catalog" description="Products appear here once they’re registered on the platform." />
              </TableEmpty>
            )}
            {all.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">
                  {p.display_name} <span className="text-xs text-muted-foreground font-mono ml-1">{p.id}</span>
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">{p.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  )
}

// ── Features ────────────────────────────────────────────────────────────────

export function TenantFeatures() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const [features, setFeatures] = useState<Feature[]>([])
  const load = () => api.get<Feature[]>(`/api/admin/features/tenant/${tenantId}`).then(setFeatures).catch(() => {})
  useEffect(() => { void load() }, [tenantId])

  async function toggle(f: Feature) {
    if (f.override_enabled !== null) {
      await api.delete(`/api/admin/features/tenant/${tenantId}/${f.feature_id}`)
    } else {
      await api.put(`/api/admin/features/tenant/${tenantId}/${f.feature_id}`, { enabled: !f.enabled })
    }
    void load()
  }

  const grouped = features.reduce<Record<string, Feature[]>>((acc, f) => {
    const cat = f.category ?? 'general'
    if (!acc[cat]) acc[cat] = []
    acc[cat]!.push(f)
    return acc
  }, {})

  return (
    <div className="p-8">
      <PageHeader title="Features" description="Plan defaults with per-tenant overrides." />
      <div className="space-y-4">
        {Object.entries(grouped).map(([cat, feats]) => (
          <Card key={cat} className="overflow-hidden">
            <CardHeader>{cat}</CardHeader>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {['Key', 'Feature', 'Status', ''].map((h, i) => <TableHead key={h || i}>{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {feats.map((f) => (
                  <TableRow key={f.feature_id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{f.feature_key}</TableCell>
                    <TableCell className="text-foreground">{f.name}</TableCell>
                    <TableCell>
                      <Badge tone={f.enabled ? 'success' : 'neutral'}>{f.enabled ? 'Enabled' : 'Disabled'}</Badge>
                      {f.override_enabled !== null && <Badge tone="warning" className="ml-1">overridden</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="text-primary hover:text-primary" onClick={() => toggle(f)}>
                        {f.override_enabled !== null ? 'Reset to plan' : (f.enabled ? 'Disable' : 'Enable')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ))}
        {features.length === 0 && (
          <Card>
            <EmptyState icon={SlidersHorizontal} title="No features defined" description="Features come from plans and the feature catalog — add them via the DB seed." />
          </Card>
        )}
      </div>
    </div>
  )
}

// ── Adapters ────────────────────────────────────────────────────────────────

const emptyAdapterForm = { adapterTypeId: '', instanceName: '', configText: '{}', secretRef: '' }

function parseConfig(text: string): Record<string, unknown> | null {
  try {
    const v: unknown = JSON.parse(text.trim() || '{}')
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

// secret_ref is a Key Vault URI — a pointer, never the secret. Anything that
// isn't an https URL is treated as a pasted secret and refused client-side
// (the API enforces the same rule).
const secretRefOk = (v: string) => v === '' || (v.startsWith('https://') && !/\s/.test(v))

export function TenantAdapters() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const { data: adapters = [], loading, error, reload } = useResource(
    () => api.get<Adapter[]>(`/api/admin/adapters/tenant/${tenantId}`), [tenantId])
  const { data: types = [], error: typesError } = useResource(
    () => api.get<AdapterType[]>('/api/admin/adapters/types'), [])

  const [dialog, setDialog] = useState<null | { mode: 'create' } | { mode: 'edit'; adapter: Adapter }>(null)
  const [form, setForm] = useState(emptyAdapterForm)
  const [removing, setRemoving] = useState<Adapter | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const selType = dialog?.mode === 'create'
    ? types.find((t) => t.id === form.adapterTypeId)
    : types.find((t) => t.id === (dialog?.mode === 'edit' ? dialog.adapter.adapter_type_id : ''))
  const configValid = parseConfig(form.configText) !== null
  const formValid = configValid && secretRefOk(form.secretRef)
    && form.instanceName.trim() !== '' && (dialog?.mode !== 'create' || form.adapterTypeId !== '')

  function openCreate() {
    save.reset()
    setForm(emptyAdapterForm)
    setDialog({ mode: 'create' })
  }
  function openEdit(a: Adapter) {
    save.reset()
    setForm({
      adapterTypeId: a.adapter_type_id,
      instanceName: a.instance_name,
      configText: JSON.stringify(a.config ?? {}, null, 2),
      secretRef: a.secret_ref ?? '',
    })
    setDialog({ mode: 'edit', adapter: a })
  }

  const save = useMutation(() => {
    const config = parseConfig(form.configText) ?? {}
    const body = { instanceName: form.instanceName.trim(), config }
    return dialog?.mode === 'edit'
      ? api.patch(`/api/admin/adapters/tenant/${tenantId}/${dialog.adapter.id}`,
          { ...body, secretRef: form.secretRef || null })
      : api.post(`/api/admin/adapters/tenant/${tenantId}`,
          { ...body, adapterTypeId: form.adapterTypeId, secretRef: form.secretRef || undefined })
  }, { fallbackError: 'Failed to save adapter' })
  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (await save.mutate() === undefined) return
    setDialog(null)
    toast.success(dialog?.mode === 'edit' ? 'Adapter updated' : 'Adapter added')
    reload()
  }

  async function setStatus(a: Adapter, status: 'configured' | 'disabled') {
    setBusy(a.id)
    try {
      await api.patch(`/api/admin/adapters/tenant/${tenantId}/${a.id}`, { status })
      toast.success(status === 'disabled' ? `${a.instance_name} disabled` : `${a.instance_name} enabled`)
      reload()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed')
    } finally { setBusy(null) }
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Adapters"
        description="Integration adapters configured for this tenant. Secrets live in Key Vault — only their URIs are stored here."
        action={<Button onClick={openCreate}>+ Add adapter</Button>}
      />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {['Instance', 'Type', 'Category', 'Secret', 'Status', ''].map((h, i) => <TableHead key={h || i}>{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableEmpty colSpan={6}><LoadingState /></TableEmpty>}
            {!loading && error && <TableEmpty colSpan={6}><ErrorState message={error} onRetry={reload} /></TableEmpty>}
            {!loading && !error && adapters.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium">{a.instance_name}</TableCell>
                <TableCell className="text-muted-foreground">{a.type_name}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{a.category}</TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {a.secret_ref ? <Badge tone="success">referenced</Badge> : <Badge tone="warning">not set</Badge>}
                </TableCell>
                <TableCell><Badge tone={statusTone(a.status)}>{a.status}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <RowActions label={`Actions for ${a.instance_name}`}>
                    <DropdownMenuContent>
                      <DropdownMenuItem onSelect={() => openEdit(a)}>Edit</DropdownMenuItem>
                      {a.status === 'disabled'
                        ? <DropdownMenuItem disabled={busy === a.id} onSelect={() => void setStatus(a, 'configured')}>Enable</DropdownMenuItem>
                        : <DropdownMenuItem disabled={busy === a.id} onSelect={() => void setStatus(a, 'disabled')}>Disable</DropdownMenuItem>}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem destructive onSelect={() => setRemoving(a)}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </RowActions>
                </TableCell>
              </TableRow>
            ))}
            {!loading && !error && adapters.length === 0 && (
              <TableEmpty colSpan={6}>
                <EmptyState icon={Plug} title="No adapters configured"
                  description="Wire this tenant's integrations (ITSM, channels, LLM, storage) here."
                  action={<Button onClick={openCreate}>+ Add adapter</Button>} />
              </TableEmpty>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialog !== null} onOpenChange={(open) => { if (!open) setDialog(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog?.mode === 'edit' ? `Edit ${dialog.adapter.instance_name}` : 'Add adapter'}</DialogTitle>
            <DialogDescription>
              Configuration is stored as JSON; secrets are provisioned in Key Vault and referenced by URI — never pasted here.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <FormError>{save.error}</FormError>
            {dialog?.mode === 'create' && (
              <Field label="Type">
                <Select value={form.adapterTypeId} onValueChange={(v) => setForm({ ...form, adapterTypeId: v })}>
                  <SelectTrigger><SelectValue placeholder="Choose an integration type" /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} <span className="text-muted-foreground text-xs">({t.category})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {dialog?.mode === 'create' && typesError && (
              <p className="-mt-2 text-xs text-destructive">Couldn&apos;t load the adapter-type catalogue: {typesError}</p>
            )}
            <Field label="Instance name">
              <Input value={form.instanceName} required
                onChange={(e) => setForm({ ...form, instanceName: e.target.value })} />
            </Field>
            <Field label="Config (JSON)">
              <textarea
                value={form.configText}
                onChange={(e) => setForm({ ...form, configText: e.target.value })}
                rows={5}
                spellCheck={false}
                className="w-full rounded-md border border-input bg-card px-3 py-2 font-mono text-xs text-foreground shadow-sm outline-none transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background"
              />
            </Field>
            {!configValid && <p className="-mt-2 text-xs text-destructive">Config must be a JSON object.</p>}
            <Field label="Secret reference (Key Vault URI)">
              <Input value={form.secretRef} placeholder="https://<vault>.vault.azure.net/secrets/<name>"
                onChange={(e) => setForm({ ...form, secretRef: e.target.value })} />
            </Field>
            {!secretRefOk(form.secretRef) && (
              <p className="-mt-2 text-xs text-destructive">That looks like a raw secret. Provision it in Key Vault and paste the secret&apos;s URI instead.</p>
            )}
            {selType && selType.secret_keys.length > 0 && (
              <p className="-mt-2 text-xs text-muted-foreground">
                {selType.name} needs: {selType.secret_keys.join(', ')} — non-secret fields go in the config, secret values in Key Vault.
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
              <Button type="submit" disabled={save.saving || !formValid}>{save.saving ? 'Saving…' : dialog?.mode === 'edit' ? 'Save' : 'Add adapter'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDestructive
        open={removing !== null}
        onOpenChange={(o) => { if (!o) setRemoving(null) }}
        title={`Delete ${removing?.instance_name}?`}
        confirmPhrase={removing?.instance_name ?? ''}
        confirmLabel="Delete adapter"
        description="Removes this integration instance and its configuration. The Key Vault secret itself is not touched. Anything using this adapter stops working immediately."
        onConfirm={async () => {
          if (!removing) return
          try {
            await api.delete(`/api/admin/adapters/tenant/${tenantId}/${removing.id}`)
            toast.success(`${removing.instance_name} deleted`)
            reload()
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Failed to delete adapter')
          }
        }}
      />
    </div>
  )
}

// ── Sign-in (IdP) ───────────────────────────────────────────────────────────

export function TenantSignIn() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [orgIdps, setOrgIdps] = useState<OrgIdp[]>([])
  const [enabled, setEnabled] = useState<Record<string, boolean>>({})
  const [allowLocal, setAllowLocal] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const loadTenant = () => api.get<Tenant>(`/api/admin/tenants/${tenantId}`).then((t) => { setTenant(t); setAllowLocal(t.allow_local_identities) }).catch(() => {})

  const loadIdps = async (orgId: string, tid: string) => {
    const idps = await api.get<OrgIdp[]>(`/api/admin/idp/org/${orgId}`).catch(() => [])
    setOrgIdps(idps)
    const states: Record<string, boolean> = {}
    await Promise.all(idps.map(async (i) => {
      const rows = await api.get<{ tenant_id: string; enabled: boolean }[]>(`/api/admin/idp/${i.id}/tenants`).catch(() => [])
      states[i.id] = rows.find((r) => r.tenant_id === tid)?.enabled ?? false
    }))
    setEnabled(states)
  }

  useEffect(() => { void loadTenant() }, [tenantId])
  useEffect(() => { if (tenant) void loadIdps(tenant.org_id, tenant.id) }, [tenant?.org_id, tenant?.id])

  async function toggleLocal(next: boolean) {
    if (!tenant) return
    setBusy('local')
    try {
      await api.put(`/api/admin/idp/tenant/${tenant.id}/local-identities`, { allowLocalIdentities: next })
      setAllowLocal(next)
      void loadTenant()
      toast.success(next ? 'Local sign-in allowed for this tenant' : 'Local sign-in disabled — tenant is SSO-only')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed')
    } finally { setBusy(null) }
  }

  async function toggleIdp(idpId: string, next: boolean) {
    if (!tenant) return
    setBusy(idpId)
    try {
      await api.put(`/api/admin/idp/${idpId}/tenants/${tenant.id}`, { enabled: next })
      setEnabled((e) => ({ ...e, [idpId]: next }))
      toast.success(next ? 'Provider enabled for this tenant' : 'Provider disabled for this tenant')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed')
    } finally { setBusy(null) }
  }

  if (!tenant) return <div className="p-8"><LoadingState /></div>
  const ssoIdps = orgIdps.filter((i) => i.kind !== 'local')

  return (
    <div className="p-8">
      <PageHeader title="Sign-in" description="How members of this tenant authenticate." />
      <div className="space-y-6">
        <Card className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-xl">
              <div className="text-sm font-medium">Local password sign-in</div>
              <p className="mt-1 text-xs text-muted-foreground">
                {allowLocal
                  ? 'On — members can be invited with a password and sign in locally. Turn this off once SSO covers everyone, so access follows your directory (joiner/leaver).'
                  : 'Off (recommended) — this tenant is SSO-only. Password invites and non-domain emails are rejected; members sign in through an enabled provider below.'}
              </p>
              {!ssoIdps.some((i) => enabled[i.id]) && !allowLocal && (
                <p className="mt-2 text-xs text-warning">No SSO provider is enabled yet — with local off, nobody can be invited into this tenant.</p>
              )}
            </div>
            <Switch checked={allowLocal} disabled={busy === 'local'} onCheckedChange={toggleLocal} label="Allow local password sign-in for this tenant" />
          </div>
        </Card>

        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="text-sm font-medium">Single sign-on providers</h3>
            <Link to={`/org/${tenant.org_id}/idp`} className="text-xs text-muted-foreground hover:text-foreground">
              Manage providers on {tenant.org_name} →
            </Link>
          </div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {['Provider', 'Kind', 'Domains', 'Status', 'Enabled here'].map((h) => <TableHead key={h}>{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {ssoIdps.map((idp) => (
                  <TableRow key={idp.id}>
                    <TableCell className="font-medium">{idp.name}</TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">{idp.kind}</TableCell>
                    <TableCell className="text-muted-foreground text-xs font-mono">{idp.domains.length ? idp.domains.join(', ') : 'any'}</TableCell>
                    <TableCell><Badge tone={statusTone(idp.status)}>{idp.status}</Badge></TableCell>
                    <TableCell><Switch checked={!!enabled[idp.id]} disabled={busy === idp.id} onCheckedChange={(v) => toggleIdp(idp.id, v)} label={`Enable ${idp.name} for this tenant`} /></TableCell>
                  </TableRow>
                ))}
                {ssoIdps.length === 0 && (
                  <TableEmpty colSpan={5}>
                    <EmptyState
                      icon={Plug}
                      title={`No SSO providers on ${tenant.org_name} yet`}
                      description="Add an identity provider on the organization, then enable it for this tenant here."
                      action={<Link to={`/org/${tenant.org_id}/idp`} className="text-sm text-primary hover:underline">Manage providers on {tenant.org_name} →</Link>}
                    />
                  </TableEmpty>
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>
    </div>
  )
}
